import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  isEmbeddedPlayerSelector,
  moreInfoVisibleSelector,
  setMoreInfoVisible,
  setUserSelectedLocale,
  userSelectedLocaleSelector,
} from '../../slices/vh-player/VHPlayerSlice';
import { virtualHumanDataSelector } from '../../slices/VirtualHumanSlice';
import VHProfileImage, {
  AvatarSizes,
} from '../atoms/VHProfileImage/VHProfileImage';
import * as Styled from './VHInfoPopup.styled';
import {
  selectChatState,
  ChatStates,
  isPreviewPlayerSelector,
} from '../../slices/chat/ChatSlice';
import { Button, Icon, Modal, Select } from '@virtidev/toolbox';
import { useVHTypeConfig } from '../vh-types/vhTypeHooks';
import { patientSelector } from '../../slices/PatientSlice';
import tracker from '../../../../core/src/helpers/tracker';
import { useVhReady } from '@vh/components/VHInfoPopup/useVhReady';
import { useVHPlayerConfig } from '@vh/components/vh-types/vhPlayerHooks';
import { useLocaleOptions } from '@core/helpers/useLocaleOptions';
import useGetVHData from '@base/utility/VHHooks/useGetVHData';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from 'i18next';
import CountryOptionLabel from '@core/components/ReactSelect/CountryOptionLabel/CountryOptionLabel';
import { GetFormattedVHPlayerTime } from '@vh/components/VHTimerV2/useCountdownTimer';
import { useTheme } from 'styled-components';
import useLocalStorage from '@vh/utility/useLocalStorage';
import { useShownMenuSessionGoals } from '@vh/components/VHInfoPopup/useShownMenuGoals';
import { useVHWebModalContext } from '@vh/components/VHWebModalBase/VHWebModalBaseContext';

/** @type {React.FC<{vhId: number; onBeforeStart: () => Promise<void>; avatarComponent?: React.ReactNode}>} */
const VHInfoPopup = ({ vhId, onBeforeStart, avatarComponent }) => {
  const moreInfoVisible = useSelector(moreInfoVisibleSelector);
  const vh = useSelector(virtualHumanDataSelector);
  const vhDBVH = useSelector(patientSelector);
  const dispatch = useDispatch();
  const onModalClose = useCallback(() => {
    dispatch(setMoreInfoVisible(false));
  }, [dispatch]);
  const [vhReady, hasChecked] = useVhReady(vhId, vh.Type);
  const [storedUserSelectedBCP47Code, setStoredUserSelectedBCP47Code] =
    useLocalStorage('activeUserLocale', '');
  const userSelectedLocale = useSelector(userSelectedLocaleSelector);
  const chatState = useSelector(selectChatState);
  const vhTypeConfig = useVHTypeConfig();
  const isEmbedded = useSelector(isEmbeddedPlayerSelector);
  const isPreviewPlayer = useSelector(isPreviewPlayerSelector);
  const [runningPreStartLoad, setRunningPreStartLoad] = useState(false);
  const playerConfig = useVHPlayerConfig();
  const {
    data: vhDBLocalesData,
    loading: vhDBLocalesLoading,
    error,
  } = useGetVHData(`locales`);

  const handleLocaleChange = useCallback(
    (newLocale) => {
      setLocale(newLocale);
      const localeObj = vhDBLocalesData?.data?.find(
        (locale) => locale.BCP47Code === newLocale.value
      );
      dispatch(setUserSelectedLocale(localeObj));
      changeLanguage(localeObj.BCP47Code);
      setStoredUserSelectedBCP47Code(localeObj.BCP47Code);
    },
    [dispatch, vhDBLocalesData, setStoredUserSelectedBCP47Code]
  );

  const { localeOptions, localeOptionsLoading, localeOptionsError } =
    useLocaleOptions(true);

  const userSelectedLocaleOption = useMemo(() => {
    return localeOptions.find((lang) => lang.value === userSelectedLocale);
  }, [localeOptions, userSelectedLocale]);

  const vhLocaleOption = useMemo(() => {
    return localeOptions.find(
      (lang) => lang.value === (vhDBVH?.lang?.BCP47Code ?? 'en-US')
    );
  }, [localeOptions, vhDBVH?.lang?.BCP47Code]);
  const [locale, setLocale] = useState(
    userSelectedLocaleOption ?? vhLocaleOption
  );

  const localStorageLocale = useMemo(() => {
    const localeObj = vhDBLocalesData?.data?.find(
      (locale) => locale.BCP47Code === storedUserSelectedBCP47Code
    );
    return localeObj;
  }, [storedUserSelectedBCP47Code, vhDBLocalesData?.data]);

  const localStorageLocaleOption = useMemo(() => {
    if (!storedUserSelectedBCP47Code || !localStorageLocale) {
      return null;
    }
    const option = localeOptions.find(
      (lang) => lang.value === localStorageLocale.BCP47Code
    );
    return option;
  }, [localeOptions, storedUserSelectedBCP47Code, localStorageLocale]);

  useEffect(() => {
    if (
      !localeOptionsLoading &&
      !localStorageLocaleOption &&
      !userSelectedLocaleOption &&
      !!vhLocaleOption
    ) {
      changeLanguage(vhLocaleOption.value);
      setLocale(vhLocaleOption);
    }
  }, [
    localStorageLocaleOption,
    localeOptionsLoading,
    userSelectedLocaleOption,
    vhLocaleOption,
  ]);

  // if locale is saved in localstorage then set it to the relevant locale object
  const hasLoadedLocalStorage = useRef(false);
  useEffect(() => {
    if (
      vhTypeConfig.allowsUserLanguageChange &&
      vhDBLocalesData?.data &&
      !!localStorageLocale?.BCP47Code &&
      !hasLoadedLocalStorage.current &&
      localStorageLocaleOption &&
      vh.ShowLanguageSelection
    ) {
      hasLoadedLocalStorage.current = true;
      dispatch(setUserSelectedLocale(localStorageLocale));
      changeLanguage(localStorageLocale.BCP47Code);
      setLocale(localStorageLocaleOption);
    }
  }, [
    dispatch,
    localeOptions,
    localStorageLocale,
    storedUserSelectedBCP47Code,
    vhDBLocalesData,
    localStorageLocaleOption,
    vhDBVH.lang?.BCP47Code,
    vhTypeConfig.allowsUserLanguageChange,
    vh.ShowLanguageSelection,
  ]);

  const handleClickStart = useCallback(async () => {
    if (onBeforeStart) {
      setRunningPreStartLoad(true);
      try {
        await onBeforeStart();
        setRunningPreStartLoad(false);
      } catch (e) {
        setRunningPreStartLoad(false);
        return;
      }
    }
    if (playerConfig.conversationControl === 'standard') {
      dispatch(vhTypeConfig.getGreetingThunk(vhDBVH)(vhId));
    }
    // can't run event if embedded due to Cross origin iframe usage
    if (!isEmbedded) {
      var event = new CustomEvent('onVHSessionStart');
      window.parent.document.dispatchEvent(event);
    }
    onModalClose();

    if (isPreviewPlayer) {
      tracker.track('vh_preview', {
        ID: +vh.ID,
        ExternalID: vhId,
      });
    }
  }, [
    onBeforeStart,
    playerConfig?.conversationControl,
    isEmbedded,
    onModalClose,
    isPreviewPlayer,
    dispatch,
    vhTypeConfig,
    vhDBVH,
    vhId,
    vh.ID,
  ]);

  const { t } = useTranslation();
  const { color } = useTheme();
  const shownGoals = useShownMenuSessionGoals();
  const VHWebModalContext = useVHWebModalContext();

  return (
    <Styled.VHInfoPopupModal
      containerRef={VHWebModalContext.current}
      show={moreInfoVisible}
      onHide={chatState === ChatStates.LOADING ? undefined : onModalClose}
      $hasFooterContent={
        chatState === ChatStates.LOADING || vh.ShowLanguageSelection
      }
      footerRender={() => {
        if (chatState !== ChatStates.LOADING && !vh.ShowLanguageSelection) {
          return <></>;
        }
        return (
          <Styled.VHInfoPopupModalFooterContent>
            {vh.ShowLanguageSelection && vhTypeConfig.canChangeLocale ? (
              <Select
                value={locale}
                options={localeOptions}
                placeholder="Select language"
                onChange={handleLocaleChange}
                menuPortalTarget={document.body}
                menuPosition={'fixed'}
                disabled={chatState !== ChatStates.LOADING}
                label="Language"
                loading={
                  localeOptionsLoading ||
                  vhDBLocalesLoading ||
                  !!error ||
                  !!localeOptionsError
                }
                formatOptionLabel={CountryOptionLabel}
              />
            ) : (
              <div />
            )}
            {chatState === ChatStates.LOADING && (
              <div>
                <Button
                  loading={!vhReady || runningPreStartLoad}
                  disabled={!vhReady}
                  color="turquoise"
                  onClick={handleClickStart}
                >
                  {t('startSession')}
                </Button>
              </div>
            )}
          </Styled.VHInfoPopupModalFooterContent>
        );
      }}
    >
      <Styled.ModalContent>
        <Styled.Heading>{vh?.Title}</Styled.Heading>
        <Styled.Cols>
          <Styled.AvatarWrapper>
            {avatarComponent ?? (
              <VHProfileImage src={vh?.ThumbnailURL} size={AvatarSizes.LARGE} />
            )}
            {vh.TimeLimit > 0 && (
              <Styled.ShadedTime>
                <Styled.ClockIconWrapper>
                  <Icon
                    type="outline"
                    size="26px"
                    color={color.turquoise}
                    icon="clock"
                  />
                </Styled.ClockIconWrapper>
                <div>{GetFormattedVHPlayerTime(vh.TimeLimit * 60)}</div>
              </Styled.ShadedTime>
            )}
          </Styled.AvatarWrapper>
          <Styled.MainCol>
            <Styled.Shaded>
              {vh?.Description}
              {/* <DynamicTranslation
                loadingComponent={
                  <Styled.TranslationLoading>
                    <LoadingIcon />
                  </Styled.TranslationLoading>
                }
                text={vh?.Description}
              /> */}
            </Styled.Shaded>
            {!vh.DisableHelp && shownGoals && shownGoals.length > 0 && (
              <Styled.Shaded>
                <Styled.SessionGoalHeader>
                  {t('howToAchieveBestScore')}
                </Styled.SessionGoalHeader>
                {shownGoals.map((shownGoal) => (
                  <Styled.SessionGoal key={shownGoal.id}>
                    <Icon
                      color={color.turquoise}
                      icon="success"
                      size="22"
                      type="outline"
                    />
                    <div>{shownGoal.text}</div>
                  </Styled.SessionGoal>
                ))}
              </Styled.Shaded>
            )}
          </Styled.MainCol>
        </Styled.Cols>
        {hasChecked && !vhReady && (
          <div>
            This virtual human is currently training, please wait or come back
            later.
          </div>
        )}
      </Styled.ModalContent>
    </Styled.VHInfoPopupModal>
  );
};

VHInfoPopup.propTypes = {
  vhId: PropTypes.number.isRequired,
  onBeforeStart: PropTypes.func.isRequired,
};

export default VHInfoPopup;
